import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getDashboardKpis, useDashboard } from '@/utils/dashboard';
import { pluralize } from '@/utils/format';
import StatCard from '@/components/base/StatCard';
import SectionCard from '@/components/base/SectionCard';
import Button from '@/components/base/Button';
import CampaignProgressTable from './components/CampaignProgressTable';
import ApplicationsNudgeCard from './components/ApplicationsNudgeCard';
import ScheduledPayoutStrip from './components/ScheduledPayoutStrip';
import ApprovalsPanel from './components/ApprovalsPanel';
import CreatorActionsPanel from './components/CreatorActionsPanel';
import NotYetActiveGroup from './components/NotYetActiveGroup';
import AlertsPanel from './components/AlertsPanel';
import AiInsightsPanel from './components/AiInsightsPanel';
import DueTodayApprovalsModal from './components/DueTodayApprovalsModal';
import LiveStatus from './components/LiveStatus';
import { RotatingWords } from '@/components/motion/Motion';
import { stagger, useSpotlight } from '@/components/motion/motionHelpers';

/** Staggers the stat cards in, one after another, on first show. */
const rise = (step: number) => ({ animationDelay: `${step * 70}ms` });

interface SummaryCounts {
  pendingApprovals: number;
  approvalsDueToday: number;
  creatorsNeedingAttention: number;
  activeCampaigns: number;
}

/** The line under the greeting: leads with what's urgent, or says all is clear. */
function summaryLine({
  pendingApprovals,
  approvalsDueToday,
  creatorsNeedingAttention,
  activeCampaigns,
}: SummaryCounts) {
  const campaigns = `${activeCampaigns} ${pluralize(activeCampaigns, 'active campaign')}`;

  if (pendingApprovals === 0 && creatorsNeedingAttention === 0) {
    return activeCampaigns > 0 ? (
      <>
        <span className="font-medium text-accent-700">All clear</span> — nothing is waiting on you
        across {campaigns}.
      </>
    ) : (
      <>
        <span className="font-medium text-accent-700">All clear.</span> Start a campaign or find
        creators to get going.
      </>
    );
  }

  const parts: ReactNode[] = [];
  if (approvalsDueToday > 0) {
    const later = pendingApprovals - approvalsDueToday;
    parts.push(
      <span key="due">
        <span className="font-medium text-primary-700">
          {approvalsDueToday} {pluralize(approvalsDueToday, 'approval')}{' '}
          {approvalsDueToday === 1 ? 'is' : 'are'} due today
        </span>
        {later > 0 ? ` (${later} more waiting)` : ''}
      </span>,
    );
  } else if (pendingApprovals > 0) {
    parts.push(
      <span key="pending">
        {pendingApprovals} {pluralize(pendingApprovals, 'approval')}{' '}
        {pendingApprovals === 1 ? 'is' : 'are'} waiting
      </span>,
    );
  }
  if (creatorsNeedingAttention > 0) {
    parts.push(
      <span key="creators">
        {creatorsNeedingAttention} {pluralize(creatorsNeedingAttention, 'creator')}{' '}
        {creatorsNeedingAttention === 1 ? 'needs' : 'need'} attention
      </span>,
    );
  }

  return (
    <>
      {parts.length === 2 ? [parts[0], ' and ', parts[1]] : parts} across {campaigns}.
    </>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [dueOpen, setDueOpen] = useState(false);
  const { user } = useAuth();
  const { data, error, updatedAt, reload } = useDashboard();
  const spotlight = useSpotlight();
  const firstName = (user?.name ?? '').split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  if (!data) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        {error ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 py-12 text-center">
            <p className="text-sm text-primary-800">
              <i className="ri-error-warning-line mr-1" />
              {error}
            </p>
            <Button variant="outline" size="sm" icon="ri-refresh-line" onClick={reload}>
              Try again
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center py-24 text-sm text-foreground-500">
            <i className="ri-loader-4-line mr-2 animate-spin" />
            Loading your dashboard…
          </div>
        )}
      </div>
    );
  }

  const stats = data.stats;
  const kpis = getDashboardKpis(data);
  // "Today's focus" cycles through what is actually waiting, from the same figures as the cards.
  const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
  const focus = [
    stats.approvalsDueToday > 0 ? `${plural(stats.approvalsDueToday, 'approval', 'approvals')} due today` : '',
    stats.creatorsNeedingAttention > 0 ? plural(stats.creatorsNeedingAttention, 'creator needs attention', 'creators need attention') : '',
    data.applications.count > 0 ? plural(data.applications.count, 'new application to review', 'new applications to review') : '',
    stats.activeCampaigns > 0 ? plural(stats.activeCampaigns, 'campaign running', 'campaigns running') : '',
  ].filter(Boolean);

  const scrollToCreatorActions = () => {
    document
      .getElementById('creator-actions-needed')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const inFlightMarkets = stats.inFlightMarkets;
  const dueToday = data.dueToday;

  // When an open-to-matching campaign has fresh interest, the spend card steps
  // aside for an action card that jumps straight to those applications.
  const applications = data.applications;
  const hasNewApplications = applications.count > 0;
  const applicationsHint =
    applications.campaigns.length === 1
      ? applications.campaigns[0].name
      : `Across ${applications.campaigns.length} open campaigns`;
  const reviewApplications = () => {
    if (applications.campaigns.length === 1) {
      navigate(`/campaigns/${applications.campaigns[0].id}?tab=applications`);
    } else {
      navigate('/campaigns');
    }
  };

  const kpiTargets: Record<string, () => void> = {
    'Active campaigns': () => navigate('/campaigns'),
    'Creators needing attention': scrollToCreatorActions,
    'Spend this month': () => navigate('/payments/transactions'),
    'Pending approvals': () => navigate('/invoices'),
  };

  const kpiDeltaTargets: Record<string, () => void> = {
    'Active campaigns': () =>
      navigate(`/campaigns?markets=${encodeURIComponent(inFlightMarkets.join(','))}`),
    'Creators needing attention': scrollToCreatorActions,
    'Spend this month': () => navigate('/payments/transactions'),
    'Pending approvals': () => setDueOpen(true),
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      {/* The welcome banner: Twiva's soft brand gradient with slow-moving colour behind it. */}
      <section
        ref={spotlight.ref}
        onPointerMove={spotlight.onPointerMove}
        onPointerLeave={spotlight.onPointerLeave}
        className="brand-gradient-soft relative overflow-hidden rounded-2xl border border-primary-100 px-5 py-6 shadow-sm md:px-8 md:py-7"
      >
        <div className="join-aurora -left-16 -top-24 h-64 w-64 bg-primary-300/15" aria-hidden="true" />
        <div className="join-aurora is-second -bottom-28 right-[10%] h-64 w-64 bg-accent-300/30" aria-hidden="true" />
        <div className="join-spotlight dash-spotlight" aria-hidden="true" />
        <div
          className="join-shapes pointer-events-none absolute -right-6 -top-8 hidden grid-cols-2 gap-2 opacity-[0.12] md:grid"
          aria-hidden="true"
        >
          <span className="h-14 w-14 rounded-xl bg-primary-600" />
          <span className="h-14 w-14 rounded-xl bg-primary-600/60" />
          <span className="h-14 w-14 rounded-xl bg-primary-600/40" />
          <span className="h-14 w-14 rounded-xl bg-primary-600/80" />
        </div>

        <div className="relative flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="join-cascade min-w-0">
            <span className="page-rule" aria-hidden="true" />
            <h1 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl" aria-label={`${greeting}, ${firstName}`}>
              {`${greeting}, ${firstName}`.split(' ').map((word, index) => (
                <span key={`${word}-${index}`} aria-hidden="true">
                  <span className="join-word" style={stagger(index)}>
                    {word}
                  </span>{' '}
                </span>
              ))}
              <span className="dash-wave" aria-hidden="true">
                👋
              </span>
            </h1>
            <p className="mt-1 text-sm text-foreground-600">{summaryLine(stats)}</p>
            {focus.length > 0 ? (
              <p className="mt-3 flex flex-wrap items-center gap-x-2 text-sm font-medium text-foreground-800" aria-label={`Today's focus: ${focus.join(', ')}`}>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/80 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-primary-700 shadow-sm">
                  <i className="ri-focus-3-line" aria-hidden="true" />
                  Today's focus
                </span>
                <RotatingWords words={focus} className="is-dark" every={3000} delay={1600} />
              </p>
            ) : null}
            <div className="mt-3">
              <LiveStatus updatedAt={updatedAt} error={error} onRetry={reload} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" icon="ri-file-add-line" className="bg-background-50/80 backdrop-blur" onClick={() => navigate('/campaigns/new')}>
              New campaign
            </Button>
            <Button variant="outline" icon="ri-send-plane-line" className="bg-background-50/80 backdrop-blur" onClick={() => navigate('/payments/send')}>
              Pay creator
            </Button>
            <Button icon="ri-sparkling-2-line" className="join-shine" onClick={() => navigate('/discovery')}>
              Find creators
            </Button>
          </div>
        </div>
      </section>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((kpi, index) => {
          if (hasNewApplications && kpi.label === 'Spend this month') {
            return (
              <div key="applications-nudge" className="animate-fade-rise" style={rise(1 + index)}>
                <ApplicationsNudgeCard
                  count={applications.count}
                  hint={applicationsHint}
                  onReview={reviewApplications}
                />
              </div>
            );
          }
          return (
            <div key={kpi.label} className="animate-fade-rise" style={rise(1 + index)}>
              <StatCard
                label={kpi.label}
                value={kpi.value}
                icon={kpi.icon}
                tone={kpi.tone}
                delta={kpi.delta}
                deltaPositive={kpi.deltaPositive}
                hint={kpi.hint}
                onClick={kpiTargets[kpi.label]}
                onDeltaClick={kpiDeltaTargets[kpi.label]}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <ScheduledPayoutStrip batches={data.scheduledBatches} />
      </div>

      <div className="mt-6">
        <SectionCard
          title="AI insights"
          subtitle="What needs a decision next"
          bodyClassName=""
        >
          <AiInsightsPanel insights={data.insights} />
        </SectionCard>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <SectionCard
          className="xl:col-span-2"
          title="Campaign progress"
          subtitle="Active and recently updated programmes"
          bodyClassName=""
          action={
            <Button variant="ghost" size="sm" onClick={() => navigate('/campaigns')}>
              View all
            </Button>
          }
        >
          <CampaignProgressTable rows={data.campaigns} />
        </SectionCard>

        <SectionCard
          title="Pending approvals"
          subtitle="Invoices, batches, content and contracts"
          bodyClassName=""
        >
          <ApprovalsPanel items={data.approvals} />
        </SectionCard>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <SectionCard
          className="xl:col-span-2 scroll-mt-24"
          title="Creator actions needed"
          subtitle="Exceptions flagged by automation on running campaigns"
          bodyClassName=""
          id="creator-actions-needed"
        >
          <CreatorActionsPanel creatorActions={data.creatorActions} />
          <NotYetActiveGroup creators={data.notYetActive} />
        </SectionCard>

        <SectionCard title="Alerts" subtitle="Risks across the portfolio" bodyClassName="">
          <AlertsPanel alerts={data.alerts} />
        </SectionCard>
      </div>

      <DueTodayApprovalsModal
        open={dueOpen}
        items={dueToday}
        onClose={() => setDueOpen(false)}
      />
    </div>
  );
}